import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Send, Brain, CheckCircle, Building, MapPin, Calendar, CalendarCheck, Eye, Target } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { JobDetailContent } from "@/components/JobDetailContent";
import CandidateMatchResults from "@/components/CandidateMatchResults";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useState, useEffect } from "react";
import { apiRequest } from "@/lib/queryClient";
import { QueryKeys, type JobDetailPublicDto, type CandidateApplicationDto, type MockInterviewSessionDto, type TypedComponentProps } from "@/types/api";
import { registerComponentMigration } from "@/types/legacy";
import type { Application, MockInterviewSession } from "@shared/schema";

// Phase 5c: Use canonical schema for consistency
import { insertApplicationSchema } from "@shared/schema";

const applicationSchema = insertApplicationSchema.pick({
  jobId: true,
  coverLetter: true,
}).extend({
  includeInterviewResults: z.boolean().default(false),
});

// Phase 5c: Component migration marker for critical user-facing component
interface JobDetailModalProps extends TypedComponentProps {
  jobId: string;
  isOpen: boolean;
  onClose: () => void;
}

export function JobDetailModal({ 
  jobId, 
  isOpen, 
  onClose,
  __typed = true,
  __phase = '5c'
}: JobDetailModalProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [applicationDialogOpen, setApplicationDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'match-results'>('details');

  // Phase 5c: Register component migration for rollback capability
  useEffect(() => {
    registerComponentMigration('JobDetailModal', '5c', () => {
      console.warn('[Phase5c] JobDetailModal rolled back to legacy types');
      // Rollback implementation would revert to untyped queries
    });
  }, []);

  // Phase 5c: Properly typed query using explicit queryFn to fix array key issue
  const { data: job, isLoading, error } = useQuery<JobDetailPublicDto>({
    queryKey: QueryKeys.jobPublic(jobId),
    queryFn: async () => {
      const response = await fetch(`/api/jobs/public/${jobId}`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to fetch job');
      return response.json();
    },
    enabled: !!jobId && isOpen,
  });

  // Phase 5c: Properly typed applications query using default fetcher
  const { data: applications = [] } = useQuery<CandidateApplicationDto[]>({
    queryKey: QueryKeys.candidateApplications(),
    enabled: !!user && user.role === 'candidate',
  });
  
  const hasApplied = applications.some((app: CandidateApplicationDto) => app.jobId === jobId);
  
  // Find the current application for this job to get interview scheduling info
  const currentApplication = applications.find((app: CandidateApplicationDto) => app.jobId === jobId);

  // Phase 5c: Properly typed interview data query using default fetcher
  const { data: interviewData = [] } = useQuery<MockInterviewSessionDto[]>({
    queryKey: QueryKeys.mockInterviewSessions(),
    enabled: !!user && user.role === 'candidate',
  });
  
  // Phase 5c: Properly typed interview find operation
  const jobInterview = interviewData.find((interview: MockInterviewSessionDto) => 
    interview.jobId === jobId
  );
  
  // Standardized button logic to match other components
  const getInterviewButtonState = () => {
    if (!jobInterview) return { text: "Practice Interview", variant: "outline" as const };
    
    if (jobInterview.status === 'completed') {
      return { text: "Retake Interview", variant: "outline" as const };
    }
    
    if (jobInterview.answers && jobInterview.answers.length > 0) {
      return { text: "Continue Interview", variant: "outline" as const };
    }
    
    return { text: "Practice Interview", variant: "outline" as const };
  };
  
  const interviewButtonState = getInterviewButtonState();
  const hasCompletedInterview = jobInterview?.status === 'completed';

  // Application form
  const applicationForm = useForm({
    resolver: zodResolver(applicationSchema),
    defaultValues: {
      jobId: jobId,
      coverLetter: '',
      includeInterviewResults: false,
    },
  });

  // Phase 5c: Submit application mutation with proper typing - fixes apiRequest parameter issue
  const submitApplicationMutation = useMutation({
    mutationFn: async (data: z.infer<typeof applicationSchema>) => {
      return apiRequest('/api/applications', { method: 'POST', body: JSON.stringify(data) });
    },
    onSuccess: () => {
      toast({
        title: 'Application submitted',
        description: 'Your application has been successfully submitted.',
      });
      queryClient.invalidateQueries({ queryKey: QueryKeys.candidateApplications() });
      setApplicationDialogOpen(false);
      applicationForm.reset();
    },
    onError: (error: unknown) => {
      // Phase 5c: Properly typed error handling - fixes 'error is of type unknown' issue
      const errorMessage = error instanceof Error ? error.message : 'Failed to submit application';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  const handleApplicationSubmit = (values: z.infer<typeof applicationSchema>) => {
    submitApplicationMutation.mutate(values);
  };



  if (isLoading) {
    return (
      <Sheet open={isOpen} onOpenChange={onClose}>
        <SheetContent side="right" className="w-[1200px] sm:max-w-none p-0 flex flex-col">
          <SheetHeader className="p-6 border-b">
            <SheetTitle>Loading Job Details...</SheetTitle>
          </SheetHeader>
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-base-primary"></div>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  if (error || !job) {
    return (
      <Sheet open={isOpen} onOpenChange={onClose}>
        <SheetContent side="right" className="w-[1200px] sm:max-w-none p-0 flex flex-col">
          <SheetHeader className="p-6 border-b">
            <SheetTitle>Job Unavailable</SheetTitle>
          </SheetHeader>
          <div className="flex flex-col items-center justify-center py-16 px-6">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
              <Building className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">This job is no longer available</h3>
            <p className="text-gray-500 text-center max-w-md mb-6">
              The job posting may have been removed or is no longer accepting applications.
            </p>
            <Button onClick={onClose} variant="outline">Close</Button>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <>
      <Sheet open={isOpen} onOpenChange={onClose}>
        <SheetContent side="right" className="w-[1200px] sm:max-w-none p-0 flex flex-col overflow-hidden">
          <SheetHeader className="sr-only">
            <SheetTitle>{job.title}</SheetTitle>
          </SheetHeader>
          <ScrollArea className="h-full flex-1 overflow-auto">
            <div className="p-6">
              {/* Header with action buttons - Added pr-10 for close button space */}
              <div className="flex justify-between items-start gap-6 mb-6 pr-10">
                <div className="flex-1">
                  <h1 className="text-2xl font-bold text-base-text mb-2">{job.title}</h1>
                  <div className="flex flex-wrap items-center gap-4 text-base-text-muted text-sm">
                    <div className="flex items-center gap-2">
                      <Building className="w-4 h-4" />
                      <span className="font-medium">{job.companyProfile?.companyName || 'Company'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      <span>{job.location}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>Posted {new Date(job.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Eye className="w-4 h-4" />
                      <span>{job.viewCount || 0} views</span>
                    </div>
                  </div>
                </div>
                
                {/* Action Buttons - Top Right with margin for close button */}
                {user?.role === 'candidate' && (
                  <div className="flex gap-3 flex-shrink-0 items-center">
                    {/* Interview Scheduled Badge */}
                    {currentApplication?.interviewScheduled?.start && (
                      <Badge className="bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300 border-0 py-1.5 px-3">
                        <CalendarCheck className="w-4 h-4 mr-1.5" />
                        Interview: {new Date(currentApplication.interviewScheduled.start).toLocaleString('en-US', { 
                          weekday: 'short', 
                          month: 'short', 
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit'
                        })}
                      </Badge>
                    )}
                    {hasApplied ? (
                      <Button variant="default" disabled>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Already Applied
                      </Button>
                    ) : (
                      <Dialog open={applicationDialogOpen} onOpenChange={setApplicationDialogOpen}>
                        <DialogTrigger asChild>
                          <Button variant="default">
                            <Send className="w-4 h-4 mr-2" />
                            Apply Now
                          </Button>
                        </DialogTrigger>
                          <DialogContent className="sm:max-w-[600px]">
                            <DialogHeader>
                              <DialogTitle>Apply for {job.title}</DialogTitle>
                            </DialogHeader>
                            <Form {...applicationForm}>
                              <form onSubmit={applicationForm.handleSubmit(handleApplicationSubmit)} className="space-y-4">
                                <FormField
                                  control={applicationForm.control}
                                  name="coverLetter"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Cover Letter (Optional)</FormLabel>
                                      <FormControl>
                                        <Textarea
                                          placeholder="Tell us why you're interested in this role..."
                                          className="min-h-[150px]"
                                          {...field}
                                        />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                
                                {hasCompletedInterview && (
                                  <FormField
                                    control={applicationForm.control}
                                    name="includeInterviewResults"
                                    render={({ field }) => (
                                      <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                                        <FormControl>
                                          <Checkbox
                                            checked={field.value}
                                            onCheckedChange={field.onChange}
                                          />
                                        </FormControl>
                                        <div className="space-y-1 leading-none">
                                          <FormLabel>
                                            Include interview results
                                          </FormLabel>
                                          <p className="text-sm text-muted-foreground">
                                            Share your mock interview performance with the employer
                                          </p>
                                        </div>
                                      </FormItem>
                                    )}
                                  />
                                )}
                                
                                <div className="flex justify-end gap-3">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setApplicationDialogOpen(false)}
                                  >
                                    Cancel
                                  </Button>
                                  <Button type="submit" disabled={submitApplicationMutation.isPending}>
                                    {submitApplicationMutation.isPending ? 'Submitting...' : 'Submit Application'}
                                  </Button>
                                </div>
                              </form>
                            </Form>
                          </DialogContent>
                        </Dialog>
                    )}
                    
                    <Button 
                      variant="outline"
                      onClick={() => {
                        // The previousJobPage is already set by the page that opened this modal
                        // Just navigate to mock interview without overriding the context
                        window.location.href = `/mock-interview/${jobId}/${encodeURIComponent(job.title)}`;
                      }}
                    >
                      <Brain className="w-4 h-4 mr-2" />
                      {interviewButtonState.text}
                    </Button>
                    
                  </div>
                )}
              </div>
              
              {/* Content with tabs when user has applied */}
              {hasApplied && currentApplication ? (
                <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'details' | 'match-results')} className="w-full">
                  <TabsList className="grid w-full grid-cols-2 mb-6">
                    <TabsTrigger value="details" data-testid="tab-job-details">
                      <Building className="w-4 h-4 mr-2" />
                      Job Details
                    </TabsTrigger>
                    <TabsTrigger value="match-results" data-testid="tab-match-results">
                      <Target className="w-4 h-4 mr-2" />
                      Match Results
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="details">
                    <JobDetailContent job={job} showHeader={false} isModal={true} />
                  </TabsContent>
                  <TabsContent value="match-results">
                    {activeTab === 'match-results' && (
                      <CandidateMatchResults applicationId={currentApplication.id} />
                    )}
                  </TabsContent>
                </Tabs>
              ) : (
                <JobDetailContent job={job} showHeader={false} isModal={true} />
              )}
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>
    </>
  );
}